// Quote engine: types and calls (console API /v1/quote-settings, /v1/catalogue,
// /v1/survey, /v1/quotes; public API /v1/proposal for the customer page).
// Shapes mirror serverless/100-api/functions/quotes (validation.py, pricing.py,
// running_costs.py, snapshot.py). docs/10-quote-engine.md.
import { useMemo } from "react";
import type { EpcCertificate, FullProperty, GrantAnswers, GrantResult, HeatLoss } from "@widget";
import { ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";
import { config } from "../config";

// ─── Catalogue ───────────────────────────────────────────────────────────────

export type Category = "heat_pump" | "cylinder" | "buffer" | "controls" | "radiator" | "ancillary" | "other";

export interface Product {
  productId: string;
  category: Category;
  brand: string;
  model: string;
  name: string;
  sku: string; // merchant's code
  mpn: string; // manufacturer's code
  supplier: string;
  costExVat: number;
  notes: string;
  datasheetUrl: string;
  priceSourceUrl: string;
  priceSeenAt: string;
  active: boolean;
  source: "starter" | "manual" | "import";
  // Heat pumps
  kw?: number;
  designOutputKw?: number | null;
  refrigerant?: string;
  phase?: "single" | "three";
  scop35?: number | null;
  scop55?: number | null;
  soundPowerDb?: number | null;
  maxFlowC?: number | null;
  dimensions?: string;
  // Cylinders and buffers
  litres?: number;
  updatedAt?: string;
}

// ─── Settings ────────────────────────────────────────────────────────────────

export interface PricingLine {
  id: string;
  label: string;
  basis: "fixed" | "per_kw" | "per_radiator";
  cost: number;
  group: "labour" | "materials" | "other";
}

export interface PricingModel {
  id: string;
  name: string;
  mode: "markup" | "margin";
  hardwareMarkupPercent: number;
  installMarkupPercent: number;
  lines: PricingLine[];
}

export interface Package {
  id: string;
  name: string;
  tier: string;
  description: string;
  highlights: string[];
  heatPumpIds: string[];
  cylinderIds: string[];
  items: { productId: string; qty: number }[];
  pricingModelId: string | null;
  active: boolean;
}

export interface Extra {
  id: string;
  name: string;
  description: string;
  basis: "fixed" | "per_unit";
  unitLabel: string;
  unitCost: number;
  unitPrice: number;
  vatRate: number;
  mode: "included" | "optional" | "off";
  defaultQty: number;
  maxQty: number;
  qtyFrom: "radiators" | null;
}

export interface Tariff {
  id: string;
  name: string;
  standingPencePerDay: number;
  bands: { startHour: number; pencePerKwh: number }[];
  offPeakSharePercent: number | null;
  sourceUrl: string;
}

export type Fuel = "gas" | "oil" | "lpg" | "electric" | "other";

export interface QuoteSettings {
  pricingModels: PricingModel[];
  packages: Package[];
  extras: Extra[];
  sizing: { marginPercent: number; litresPerPerson: number; minCylinderL: number; defaultFlowTempC: number; includeCylinder: boolean };
  vat: { installRate: number };
  rounding: number;
  runningCosts: {
    enabled: boolean;
    useEpcDemand: boolean;
    efficiencySource: "product" | "mcs031";
    degreeDays: number;
    hotWaterLitresPerPersonDay: number;
    hotWaterDeltaT: number;
    hotWaterLossPercent: number;
    hotWaterScop: number;
    gasDisconnected: boolean;
    fuels: Record<Fuel, { pencePerKwh: number; standingPencePerDay: number; efficiencyPercent: number }>;
    tariffs: Tariff[];
    defaultTariffId: string | null;
    co2: Record<"electricity" | "gas" | "oil" | "lpg", number>;
    note: string;
  };
  finance: { enabled: boolean; lender: string; aprPercent: number; termsMonths: number[]; minLoan: number; maxLoan: number; depositPercent: number; notice: string };
  deposit: { enabled: boolean; type: "fixed" | "percent"; value: number; note: string };
  proposal: {
    validityDays: number;
    priceDisplay: "itemised" | "grouped" | "total";
    intro: string;
    whatsIncluded: string[];
    whyChooseUs: string[];
    terms: string;
    termsVersion: string;
    acceptanceText: string;
    showHeatLoss: boolean;
    showRunningCosts: boolean;
    emailSubject: string;
    emailMessage: string;
    brand: { primary: string; accent: string; logoUrl: string };
  };
  notifyEmails: string[];
}

// ─── Quotes ──────────────────────────────────────────────────────────────────

export type QuoteStatus = "draft" | "sent" | "viewed" | "accepted" | "declined";

export interface Totals {
  net: number;
  vat: number;
  gross: number;
  grant: number;
  payable: number;
}

export interface PricedLine {
  group: "equipment" | "installation" | "extras" | "other" | "discount";
  label: string;
  qty: number;
  cost: number;
  net: number;
  vat: number;
  vatRate: number;
  productId?: string;
  extraId?: string;
  basis?: string;
}

export interface RunningCosts {
  spaceKwh: number;
  waterKwh: number;
  spaceSource: string;
  waterSource: string;
  flowTempC: number;
  scop: number;
  scopSource: string;
  hotWaterScop: number;
  heatPumpKwh: number;
  current: { fuel: string; efficiencyPercent: number; pencePerKwh: number; fuelKwh: number; annualCost: number; standingSaved: number };
  tariffs: { id: string; name: string; pencePerKwh: number; annualCost: number; saving: number }[];
  defaultTariffId: string | null;
  co2: { currentKg: number; heatPumpKg: number; savedKg: number };
}

export interface PricedOption {
  id: string;
  name: string;
  tier: string;
  description: string;
  highlights: string[];
  available: boolean;
  warnings: string[];
  heatPump?: Partial<Product>;
  cylinder?: Partial<Product> | null;
  requiredKw?: number | null;
  requiredCylinderL?: number;
  lines: PricedLine[];
  totals: (Totals & { cost: number; profit: number; marginPercent: number; markupPercent: number }) | null;
  runningCosts: RunningCosts | null;
}

export interface SurveyResult {
  heatLoss: HeatLoss | null;
  noEstimate: string | null;
  grant: GrantResult;
  designTemps: { internalC: number; externalC: number; location: string; source: string };
  country: string | null;
  postcode: string;
  demand: { spaceHeatingKwh: number | null; waterHeatingKwh: number | null };
  certificate: EpcCertificate | null;
}

export interface Survey {
  property: FullProperty | null;
  grantAnswers: GrantAnswers;
  includeGrant: boolean;
  heatLossOverrideKw: number | null;
  overrideNote: string;
  occupants: number | null;
  flowTempC: number | null;
  result?: SurveyResult | null;
}

export interface Selection {
  packageIds: string[];
  recommendedId: string | null;
  choices: Record<string, { heatPumpId?: string; cylinderId?: string }>;
  extras: { extraId: string; mode: "included" | "optional" | "off"; qty: number | null }[];
  customLines: { id: string; label: string; qty: number; cost: number; price: number; vatRate: number }[];
  markupOverridePercent: number | null;
  discount: { type: "fixed" | "percent"; value: number; label: string };
}

export interface Acceptance {
  optionId: string;
  optionName: string;
  extras: { extraId: string; name: string; qty: number; net: number; vat: number }[];
  totals: Totals;
  tariffId?: string | null;
  payment?: "cash" | "finance";
  financeMonths?: number | null;
  name: string;
  signedAt: string;
  ip?: string;
  userAgent?: string;
  offline?: boolean;
  note?: string;
}

export interface Quote {
  quoteId: string;
  reference: string;
  status: QuoteStatus;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  customer: { name: string; email: string; phone: string };
  address: { line: string; uprn: string; postcode: string };
  survey: Survey;
  selection: Selection;
  content: { intro: string; notes: string; validUntil: string };
  source?: { widgetId?: string; uprn?: string; duplicateOf?: string };
  computed: {
    options: PricedOption[];
    recommendedId: string | null;
    grant: number;
    designKw: number | null;
    occupants: number;
    requiredCylinderL: number;
    computedAt: string;
  };
  summary?: { kw: number | null; optionName: string | null; payable: number | null; marginPercent: number | null; options: number };
  publish?: { url?: string; sentAt?: string; firstSentAt?: string; sentTo?: string; emailed?: boolean; sentCount?: number; viewedAt?: string; lastViewedAt?: string; views?: number; notifyEmails?: string[] };
  acceptance?: Acceptance | null;
  decline?: { reason: string; at: string; offline?: boolean } | null;
  changedSinceSent?: boolean;
  hasSnapshot?: boolean;
}

export type QuoteSummary = Pick<Quote, "quoteId" | "reference" | "status" | "customer" | "address" | "createdAt" | "updatedAt" | "publish" | "summary" | "changedSinceSent"> & {
  acceptance?: { totals: Totals; optionName: string };
};

// ─── The customer's view (snapshot.py) ───────────────────────────────────────

export interface ProposalOption {
  id: string;
  name: string;
  tier: string;
  description?: string;
  highlights?: string[];
  available: boolean;
  heatPump?: { brand?: string; model?: string; name?: string; kw?: number; refrigerant?: string; scop35?: number; scop55?: number; soundPowerDb?: number; dimensions?: string; datasheetUrl?: string };
  cylinder?: { brand?: string; name?: string; litres?: number; datasheetUrl?: string } | null;
  lines?: { group: string; label: string; qty: number | null; net?: number; vat?: number; vatRate?: number }[];
  groups?: { group: string; label: string; net: number; vat: number }[];
  totals?: Totals;
  runningCosts?: RunningCosts | null;
}

export interface Proposal {
  reference: string;
  /** True until the installer enters their survey heat loss */
  subjectToSurvey?: boolean;
  url: string;
  issuedAt: string;
  validUntil: string;
  customer: { name: string };
  address: { line: string };
  company: { companyName: string; tagline: string; phone: string; email: string; website: string; address: string; companyNumber: string; vatNumber: string; privacyUrl: string };
  brand: { primary?: string; accent?: string; logoUrl?: string };
  intro: string;
  whatsIncluded: string[];
  whyChooseUs: string[];
  priceDisplay: "itemised" | "grouped" | "total";
  recommendedId: string | null;
  options: ProposalOption[];
  optionalExtras: { id: string; name: string; description: string; unitPrice: number; vatRate: number; perUnit: boolean; unitLabel: string; defaultQty: number; maxQty: number }[];
  grant: { amount: number; scheme: string; checks: { text: string; status: string; detail: string }[] } | null;
  heatLoss: {
    kw: number;
    fromSurvey: boolean;
    surveyNote: string;
    estimateKw: number | null;
    level: string | null;
    method: string | null;
    internalC: number | null;
    externalC: number | null;
    designLocation: string | null;
    elements: { element: string; area?: number; u?: number; wPerK: number; source?: string }[];
  } | null;
  property: { floorArea?: number; bedrooms?: number; kind?: string; builtForm?: string; fuel?: string; epc: { band?: string; number?: string; lodgedDate?: string } | null };
  runningCosts: { tariffs: { id: string; name: string }[]; defaultTariffId: string | null; note: string } | null;
  finance: { enabled: boolean; lender: string; aprPercent: number; termsMonths: number[]; minLoan: number; maxLoan: number | null; depositPercent: number; notice: string } | null;
  deposit: { enabled: boolean; type: "fixed" | "percent"; value: number; note: string } | null;
  terms: { text: string; version: string };
  acceptanceText: string;
}

/** The QuoteFlow plan and the quote terms (GET /v1/quote-access). */
export interface QuoteAccess {
  entitled: boolean;
  termsVersion: string;
  termsAccepted: boolean;
  acceptance: { version: string; acceptedAt: string; acceptedByEmail: string } | null;
}

export interface ProposalState {
  proposal: Proposal;
  status: QuoteStatus;
  validUntil: string;
  expired: boolean;
  acceptance: Acceptance | null;
  decline: { at: string } | null;
}

// ─── Calls ───────────────────────────────────────────────────────────────────

async function request<T>(base: string, method: string, path: string, token: string | null, body?: unknown): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    method,
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, json.error ?? json.message ?? `Request failed (${res.status})`);
  return json as T;
}

/** The customer quote page's calls (public API, no sign-in: the link's token is the key). */
export const proposalApi = {
  get: (token: string) => request<ProposalState>(config.publicApiBaseUrl, "GET", `/v1/proposal/${encodeURIComponent(token)}`, null),
  accept: (
    token: string,
    body: { optionId: string; extras: { extraId: string; qty: number }[]; tariffId?: string | null; payment: "cash" | "finance"; financeMonths?: number | null; name: string; agree: boolean },
  ) => request<{ ok: boolean; acceptance: Acceptance }>(config.publicApiBaseUrl, "POST", `/v1/proposal/${encodeURIComponent(token)}/accept`, null, body),
  decline: (token: string, reason: string) => request<{ ok: boolean }>(config.publicApiBaseUrl, "POST", `/v1/proposal/${encodeURIComponent(token)}/decline`, null, { reason }),
};

export type QuoteInputs = Partial<Pick<Quote, "customer" | "address" | "survey" | "selection" | "content">> & { refresh?: boolean };

/** Signed-in quote engine calls. */
export function useQuotesApi() {
  const { idToken } = useAuth();
  return useMemo(() => {
    const call = <T,>(method: string, path: string, body?: unknown) => request<T>(config.apiBaseUrl, method, path, idToken, body);
    return {
      getAccess: () => call<QuoteAccess>("GET", "/v1/quote-access"),
      acceptTerms: (version: string) => call<QuoteAccess>("POST", "/v1/quote-terms/accept", { version }),
      getSettings: () => call<{ settings: QuoteSettings; saved: boolean }>("GET", "/v1/quote-settings"),
      saveSettings: (settings: QuoteSettings) => call<{ settings: QuoteSettings; saved: boolean }>("PUT", "/v1/quote-settings", { settings }),
      loadStarter: () => call<{ added: number; settings: QuoteSettings; products: Product[] }>("POST", "/v1/quote-settings/starter", {}),
      listProducts: () => call<{ products: Product[] }>("GET", "/v1/catalogue").then((r) => r.products),
      createProduct: (product: Partial<Product>) => call<{ product: Product }>("POST", "/v1/catalogue", { product }).then((r) => r.product),
      updateProduct: (product: Product) => call<{ product: Product }>("PUT", `/v1/catalogue/${encodeURIComponent(product.productId)}`, { product }).then((r) => r.product),
      deleteProduct: (id: string) => call<unknown>("DELETE", `/v1/catalogue/${encodeURIComponent(id)}`),
      importProducts: (rows: Record<string, unknown>[], updateExisting: boolean) =>
        call<{ created: number; updated: number; errors: { row: number; error: string }[]; products: Product[] }>("POST", "/v1/catalogue/import", { rows, updateExisting }),
      surveyAddresses: (postcode: string) => call<{ addresses: { id: string; line: string }[]; sampleData?: boolean }>("GET", `/v1/survey/addresses?postcode=${encodeURIComponent(postcode)}`),
      surveyProperty: (uprn: string, line: string) =>
        call<{ property: unknown; full: { certificate: EpcCertificate; property: Partial<FullProperty> & { baths?: number | null }; country: string | null } | null; demand: SurveyResult["demand"] }>(
          "GET",
          `/v1/survey/property/${encodeURIComponent(uprn)}?line=${encodeURIComponent(line)}`,
        ),
      listQuotes: () => call<{ quotes: QuoteSummary[] }>("GET", "/v1/quotes").then((r) => r.quotes),
      createQuote: (body: { customer: Quote["customer"]; address: Quote["address"]; survey: Partial<Survey>; source?: Quote["source"] }) =>
        call<{ quote: Quote }>("POST", "/v1/quotes", body).then((r) => r.quote),
      getQuote: (id: string) => call<{ quote: Quote }>("GET", `/v1/quotes/${encodeURIComponent(id)}`).then((r) => r.quote),
      updateQuote: (id: string, patch: QuoteInputs) => call<{ quote: Quote }>("PUT", `/v1/quotes/${encodeURIComponent(id)}`, patch).then((r) => r.quote),
      deleteQuote: (id: string) => call<unknown>("DELETE", `/v1/quotes/${encodeURIComponent(id)}`),
      previewQuote: (id: string) => call<{ proposal: Proposal }>("POST", `/v1/quotes/${encodeURIComponent(id)}/preview`, {}).then((r) => r.proposal),
      sendQuote: (id: string, body: { email: boolean; to?: string; subject?: string; message?: string }) =>
        call<{ quote: Quote }>("POST", `/v1/quotes/${encodeURIComponent(id)}/send`, body).then((r) => r.quote),
      withdrawQuote: (id: string) => call<{ quote: Quote }>("POST", `/v1/quotes/${encodeURIComponent(id)}/withdraw`, {}).then((r) => r.quote),
      duplicateQuote: (id: string) => call<{ quote: Quote }>("POST", `/v1/quotes/${encodeURIComponent(id)}/duplicate`, {}).then((r) => r.quote),
      setStatus: (id: string, body: { status: "accepted" | "declined"; note: string; optionId?: string }) =>
        call<{ quote: Quote }>("POST", `/v1/quotes/${encodeURIComponent(id)}/status`, body).then((r) => r.quote),
      /** Upload the quote logo (PNG/JPEG/WebP ≤ 1 MB) straight to S3; returns its public URL. */
      uploadLogo: async (file: File) => {
        const { upload, publicUrl } = await call<{ upload: { url: string; fields: Record<string, string> }; publicUrl: string }>("POST", "/v1/quote-settings/logo", {
          contentType: file.type,
          size: file.size,
        });
        const form = new FormData();
        for (const [k, v] of Object.entries(upload.fields)) form.append(k, v);
        form.append("file", file);
        const res = await fetch(upload.url, { method: "POST", body: form });
        if (!res.ok) throw new ApiError(res.status, "The upload didn't work. Please try a PNG, JPEG or WebP under 1 MB.");
        return publicUrl;
      },
    };
  }, [idToken]);
}
